import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import MainLayout from '../layouts/MainLayout';
import { 
    Box, 
    Button, 
    CircularProgress, 
    Typography,
    Card,
    Alert,
    TextField,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Paper
} from '@mui/material';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Save, ArrowBack } from '@mui/icons-material';
import {getTemplateMailing, createTemplateMailing, updateTemplateMailing } from "../auth";

const TemplateMailingForm: React.FC = () => {
    const [type, setType] = useState('');
    const [libelle, setLibelle] = useState('');
    const [content, setContent] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    
    const navigate = useNavigate();
    const { id } = useParams<{ id: string }>();
    const isEditMode = Boolean(id);

    useEffect(() => {
        if (isEditMode && id) {
            fetchTemplateMailing();
        }
    }, [isEditMode, id]);

    const fetchTemplateMailing = async () => {
        try {
            setLoading(true);
            const response = await getTemplateMailing();
            setType(response.type);
            setLibelle(response.libelle);
            setContent(response.content);
            setError(null);
        } catch (error) {
            setError("Erreur lors de la récupération du modèle");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setLoading(true);
            setError(null);
            const data = { type, libelle, content };
            
            if (isEditMode && id) {
                await updateTemplateMailing(Number(id), data);
            } else {
                await createTemplateMailing(data);
            }
            
            setSuccess("Modèle enregistré avec succès");
            setTimeout(() => {
                navigate('/parametrages/template-mailing');
            }, 1500);
        } catch (error) {
            setError("Erreur lors de l'enregistrement du modèle");
            console.error('Erreur:', error);
        } finally {
            setLoading(false);
        }
    };

    const quillModules = {
        toolbar: [
            [{ 'header': [1, 2, 3, false] }],
            ['bold', 'italic', 'underline', 'strike'],
            [{ 'color': [] }, { 'background': [] }],
            [{ 'list': 'ordered'}, { 'list': 'bullet' }],
            ['link', 'image'],
            ['clean']
        ],
    };

    return (
        <MainLayout>
            <Box className="p-6 space-y-6">
                {/* En-tête */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    <Box className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
                        <div>
                            <Typography variant="h4" className="text-emerald-800 font-bold mb-2">
                                {isEditMode ? 'Modifier le modèle de mail' : 'Créer un modèle de mail'}
                            </Typography>
                            <Typography variant="body1" className="text-emerald-600">
                                {isEditMode ? 'Modifiez les informations du modèle' : 'Créez un nouveau modèle de mail'}
                            </Typography>
                        </div>
                        <Box className="flex gap-2">
                            <Button
                                variant="outlined"
                                onClick={() => navigate('/parametrages/template-mailing')}
                                startIcon={<ArrowBack />}
                                className="border-emerald-600 text-emerald-600 hover:bg-emerald-50"
                            >
                                Retour
                            </Button>
                            <Button
                                variant="contained"
                                onClick={handleSave}
                                startIcon={<Save />}
                                disabled={loading}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                            >
                                Enregistrer
                            </Button>
                        </Box>
                    </Box>
                </Card>

                {/* Messages */}
                {error && (
                    <Alert 
                        severity="error" 
                        className="rounded-lg border border-red-100"
                        onClose={() => setError(null)}
                    >
                        {error}
                    </Alert>
                )}
                {success && (
                    <Alert 
                        severity="success" 
                        className="rounded-lg border border-green-100"
                        onClose={() => setSuccess(null)}
                    >
                        {success}
                    </Alert>
                )}

                {/* Formulaire */}
                <Card className="bg-white/80 backdrop-blur-sm p-6">
                    {loading ? (
                        <Box className="flex justify-center items-center p-12">
                            <CircularProgress className="text-emerald-600" />
                        </Box>
                    ) : (
                        <Box className="space-y-6">
                            <FormControl fullWidth>
                                <InputLabel>Type de mail</InputLabel>
                                <Select
                                    value={type}
                                    onChange={(e) => setType(e.target.value)}
                                    label="Type de mail"
                                >
                                    <MenuItem value="WELCOME">Bienvenue</MenuItem>
                                    <MenuItem value="NOTIFICATION">Notification</MenuItem>
                                    <MenuItem value="ALERT">Alerte</MenuItem>
                                    <MenuItem value="REMINDER">Rappel</MenuItem>
                                </Select>
                            </FormControl>

                            <TextField
                                fullWidth
                                label="Libellé"
                                value={libelle}
                                onChange={(e) => setLibelle(e.target.value)}
                                variant="outlined"
                            />

                            <Box className="space-y-2">
                                <Typography variant="subtitle1" className="text-gray-700">
                                    Contenu du mail
                                </Typography>
                                <Paper className="min-h-[400px]">
                                    <ReactQuill
                                        theme="snow"
                                        value={content}
                                        onChange={setContent}
                                        modules={quillModules}
                                        className="h-[350px]"
                                    />
                                </Paper>
                            </Box>
                        </Box>
                    )}
                </Card>
            </Box>
        </MainLayout>
    );
};

export default TemplateMailingForm;